import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FUEL_TYPES, FUEL_TYPE_LABELS, OMCS, OMC_LABELS, type FuelType, type Omc } from '@shared/domain';
import { checkTaxIds } from '@shared/taxId';
import { whatsappUrl } from '@shared/invites';
import { api } from '../../api';
import { useSession, type Me } from '../../session';
import { Dialog, ErrorBox, Field, MobileInput, dt, useBusy } from '../../ui';

export type Side = 'BUYER' | 'SUPPLIER';
export type Direction = 'SUPPLIER_INVITES_BUYER' | 'BUYER_INVITES_SUPPLIER';

export const invitedSide = (d: Direction): Side => (d === 'BUYER_INVITES_SUPPLIER' ? 'SUPPLIER' : 'BUYER');
const RESEND_SECONDS = 30;

/** States and union territories, for business addresses. */
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

/**
 * Prove a mobile number with a 6-digit SMS code. The only use of SMS codes for
 * people: sign-in itself is always Google.
 */
export function PhoneVerify({ hint, onVerified }: { hint?: ReactNode; onVerified: () => Promise<void> | void }) {
  const channels = useQuery({ queryKey: ['code-channels'], queryFn: () => api.get<{ whatsappPhone: boolean }>('/api/auth/config', { orgId: null }) });
  const whatsapp = channels.data?.whatsappPhone === true;
  const [delivery, setDelivery] = useState<{ deliveryChannel: 'SMS' | 'WHATSAPP'; fallback?: boolean }>({ deliveryChannel: 'SMS' });
  const [phone, setPhone] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [wait, setWait] = useState(0);
  const { busy, error, setError, run } = useBusy();
  useEffect(() => {
    if (!wait) return;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const send = (channel: 'SMS' | 'WHATSAPP' = delivery.deliveryChannel) => run(async () => {
    const r = await api.post<{ challengeId: string; deliveryChannel: 'SMS' | 'WHATSAPP'; fallback?: boolean }>('/api/me/phone/start', { phone, channel, whatsappConsent: channel === 'WHATSAPP' }, { orgId: null });
    setDelivery({ deliveryChannel: r.deliveryChannel ?? 'SMS', fallback: r.fallback });
    setChallenge(r.challengeId);
    setCode('');
    setWait(RESEND_SECONDS);
  });
  const verify = (value: string) => run(async () => {
    await api.post('/api/me/phone/verify', { challengeId: challenge, code: value }, { orgId: null });
    await onVerified();
  });

  if (!challenge) {
    return (
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void send(whatsapp ? 'WHATSAPP' : 'SMS'); }}>
        <Field label="Your mobile number" hint={whatsapp ? 'Choose where to receive your 6-digit code.' : 'We will send a 6-digit code by SMS.'}>
          <MobileInput value={phone} onChange={setPhone} required autoFocus />
        </Field>
        {hint ? <p className="muted">{hint}</p> : null}
        <ErrorBox error={error} />
        {whatsapp ? <p className="muted">By choosing WhatsApp, you agree to receive this verification code from SuperIndent on WhatsApp. If it cannot be sent, we will try SMS.</p> : null}
        <button className="btn primary block" disabled={busy}>{busy ? 'Sending…' : whatsapp ? 'Send code on WhatsApp' : 'Send code'}</button>
        {whatsapp ? <button type="button" className="btn block" disabled={busy || phone.length !== 10} onClick={() => void send('SMS')}>Send by SMS instead</button> : null}
      </form>
    );
  }
  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); void verify(code); }}>
      {delivery.fallback ? <div className="alert">WhatsApp was unavailable. Check your SMS messages instead.</div> : null}
      <Field label={`${delivery.deliveryChannel === 'WHATSAPP' ? 'Check WhatsApp on' : 'Code sent to'} +91 ${phone}`} hint={delivery.deliveryChannel === 'WHATSAPP' ? 'Delivery may take a moment. Enter the code here when it arrives.' : 'Check your SMS messages.'}>
        <input
          className="big-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          autoFocus
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '');
            setCode(v);
            if (v.length === 6 && !busy) void verify(v);
          }}
        />
      </Field>
      <ErrorBox error={error} />
      <button className="btn primary block" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Confirm'}</button>
      <div className="row between">
        <button type="button" className="btn ghost small" disabled={busy || wait > 0} onClick={() => void send()}>{wait > 0 ? `Resend code in ${wait}s` : 'Resend code'}</button>
        <button type="button" className="btn ghost small" disabled={busy} onClick={() => { setChallenge(null); setCode(''); setError(null); }}>Change number</button>
      </div>
      {delivery.deliveryChannel === 'WHATSAPP' ? <button type="button" className="btn block" disabled={busy || wait > 0} onClick={() => void send('SMS')}>{wait > 0 ? `Use SMS instead in ${wait}s` : 'Use SMS instead'}</button> : null}
    </form>
  );
}

export interface BusinessInput {
  org: { name: string; legalType: string; address: { line1: string; city: string; state: string; pincode: string }; gstin?: string; pan?: string };
  location?: { name: string; code: string; address: string; fuelTypes: FuelType[]; omc: Omc };
}

/** Business details for a new fleet or pump. A pump also names its first location. */
export function BusinessForm({ side, defaultName = '', submitLabel, onSubmit }: { side: Side; defaultName?: string; submitLabel: string; onSubmit: (v: BusinessInput) => Promise<unknown> }) {
  const [f, setF] = useState({ name: defaultName, legalType: 'SOLE_PROPRIETOR', line1: '', city: '', state: '', pincode: '', gstin: '', pan: '', pumpName: '', pumpCode: 'MAIN', omc: '' });
  const [fuels, setFuels] = useState<FuelType[]>(['PETROL', 'DIESEL']);
  const { busy, error, run } = useBusy();
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const pump = side === 'SUPPLIER';
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          const problem = checkTaxIds({ gstin: f.gstin, pan: f.pan, state: f.state }, { gstinRequired: pump });
          if (problem) throw new Error(problem);
          await onSubmit({
          org: {
            name: f.name.trim(),
            legalType: f.legalType,
            address: { line1: f.line1, city: f.city, state: f.state, pincode: f.pincode },
            gstin: f.gstin.trim() || undefined,
            pan: f.pan.trim() || undefined,
          },
          location: pump ? { name: f.pumpName.trim() || f.name.trim(), code: f.pumpCode, address: `${f.line1}, ${f.city}`, fuelTypes: fuels, omc: f.omc as Omc } : undefined,
          });
        });
      }}
    >
      <Field label={pump ? 'Pump business name' : 'Fleet / company name'}><input value={f.name} onChange={set('name')} required minLength={2} autoComplete="organization" /></Field>
      <Field label="Type">
        <select value={f.legalType} onChange={set('legalType')}>
          <option value="SOLE_PROPRIETOR">Proprietor (one owner)</option>
          <option value="PARTNERSHIP">Partnership</option>
          <option value="COMPANY">Private / public company</option>
          <option value="LLP">LLP</option>
          <option value="OTHER">Other</option>
        </select>
      </Field>
      <Field label="Address"><input value={f.line1} onChange={set('line1')} required minLength={2} autoComplete="street-address" /></Field>
      <div className="grid three">
        <Field label="City"><input value={f.city} onChange={set('city')} required minLength={2} autoComplete="address-level2" /></Field>
        <Field label="State">
          <select value={f.state} onChange={set('state')} required autoComplete="address-level1">
            <option value="" disabled>Choose…</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Pincode"><input inputMode="numeric" pattern="\d{6}" maxLength={6} value={f.pincode} onChange={set('pincode')} required autoComplete="postal-code" /></Field>
      </div>
      {pump ? (
        <>
          <h3>Your pump</h3>
          <div className="grid two">
            <Field label="Pump name" hint="As customers know it. Leave empty to use the business name."><input value={f.pumpName} onChange={set('pumpName')} /></Field>
            <Field label="Short code" hint="Up to 12 letters, e.g. MAIN or NH48."><input value={f.pumpCode} onChange={set('pumpCode')} required maxLength={12} /></Field>
          </div>
          <Field label="Oil company" hint="The company whose brand and fuel your pump sells.">
            <select value={f.omc} onChange={set('omc')} required>
              <option value="" disabled>Choose…</option>
              {OMCS.map((o) => <option key={o} value={o}>{OMC_LABELS[o]}</option>)}
            </select>
          </Field>
          <fieldset className="stack plain-fieldset">
            <legend>Fuels sold</legend>
            <div className="row">
              {FUEL_TYPES.map((t) => (
                <label key={t} className="check">
                  <input type="checkbox" checked={fuels.includes(t)} onChange={(e) => setFuels(e.target.checked ? [...fuels, t] : fuels.filter((x) => x !== t))} /> {FUEL_TYPE_LABELS[t]}
                </label>
              ))}
            </div>
          </fieldset>
        </>
      ) : null}
      <div className="grid two">
        <Field label={pump ? 'GSTIN' : 'GSTIN (if registered)'} hint="15 characters. Checked for typing mistakes, then confirmed by our team."><input value={f.gstin} onChange={set('gstin')} maxLength={15} required={pump} autoCapitalize="characters" /></Field>
        <Field label="PAN" hint="Business PAN, 10 characters."><input value={f.pan} onChange={set('pan')} maxLength={10} required autoCapitalize="characters" /></Field>
      </div>
      <ErrorBox error={error} />
      <button className="btn primary block" disabled={busy || (pump && !fuels.length)}>{busy ? 'Saving…' : submitLabel}</button>
    </form>
  );
}

interface AcceptResult { orgId: string; relationshipId: string; side: Side }

/**
 * Accept an invitation into an existing business of the right side, or a new
 * one. The pump sets credit terms next; the fleet waits for them.
 */
export function AcceptInvitation({ direction, defaultName, accept }: { direction: Direction; defaultName?: string; accept: (body: { existingOrgId?: string } & Partial<BusinessInput> & { newOrg?: BusinessInput['org'] }) => Promise<AcceptResult> }) {
  const { me, refresh, choose } = useSession();
  const nav = useNavigate();
  const side = invitedSide(direction);
  const adminRole = side === 'SUPPLIER' ? 'SUPPLIER_ADMIN' : 'BUYER_ADMIN';
  const owned = me?.memberships.filter((m) => m.roles.includes(adminRole)) ?? [];
  const [target, setTarget] = useState<string>(owned[0]?.orgId ?? 'new');
  const { busy, error, run } = useBusy();
  const finish = async (r: AcceptResult) => {
    await refresh();
    choose(r.orgId);
    nav(r.side === 'SUPPLIER' ? `/supplier/customers/${r.relationshipId}` : '/buyer/suppliers');
  };
  return (
    <div className="stack">
      {owned.length ? (
        <fieldset className="stack plain-fieldset">
          <legend>Connect which business?</legend>
          {owned.map((m) => (
            <label key={m.orgId} className="check"><input type="radio" name="accept-target" checked={target === m.orgId} onChange={() => setTarget(m.orgId)} /> {m.orgName}</label>
          ))}
          <label className="check"><input type="radio" name="accept-target" checked={target === 'new'} onChange={() => setTarget('new')} /> A new {side === 'SUPPLIER' ? 'pump business' : 'fleet / company'}</label>
        </fieldset>
      ) : null}
      {target === 'new' ? (
        <BusinessForm side={side} defaultName={defaultName} submitLabel="Accept and create my business" onSubmit={async (v) => finish(await accept({ newOrg: v.org, location: v.location }))} />
      ) : (
        <>
          <ErrorBox error={error} />
          <button className="btn primary block" disabled={busy} onClick={() => void run(async () => finish(await accept({ existingOrgId: target })))}>{busy ? 'Connecting…' : 'Accept invitation'}</button>
        </>
      )}
    </div>
  );
}

/** Invitations the signed-in person received, accepted in the app. */
export function PendingInvitations({ invitations }: { invitations: Me['pendingInvitations'] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = invitations.find((i) => i.id === openId);
  if (!invitations.length) return null;
  return (
    <div className="card">
      <h2>Invitations for you</h2>
      <ul className="list">
        {invitations.map((i) => (
          <li key={i.id} className="row between">
            <div>
              <strong>{i.fromName}</strong>
              <div><small>{i.direction === 'BUYER_INVITES_SUPPLIER' ? 'A fleet wants to buy fuel on credit at your pump.' : 'A pump wants to give your fleet fuel on credit.'} Expires {dt(i.expiresAt)}.</small></div>
            </div>
            <button className="btn primary" onClick={() => setOpenId(i.id)}>Review</button>
          </li>
        ))}
      </ul>
      <Dialog open={Boolean(open)} title={open ? `Connect with ${open.fromName}` : ''} onClose={() => setOpenId(null)}>
        {open ? (
          <AcceptInvitation
            direction={open.direction}
            defaultName={open.businessName}
            accept={(body) => api.post<AcceptResult>(`/api/me/invitations/${open.id}/accept`, body, { orgId: null })}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

interface SentInvitation { id: string; businessName: string; contactName: string; phoneMasked: string; status: string; createdAt: string; relationshipId?: string }

const INVITE_COPY: Record<Direction, { path: string; button: string; title: string; name: string; share: string }> = {
  SUPPLIER_INVITES_BUYER: { path: '/api/supplier/invitations', button: '+ Invite a fleet', title: 'Invite a credit customer', name: 'Fleet / company name', share: 'Please join SuperIndent for our fuel credit records' },
  BUYER_INVITES_SUPPLIER: { path: '/api/buyer/invitations', button: '+ Invite a pump', title: 'Invite a pump you buy from', name: 'Pump business name', share: 'Please join SuperIndent so we can record fuel on credit at your pump' },
};

const useSentInvitations = (direction: Direction) =>
  useQuery({ queryKey: ['invitations', direction], queryFn: () => api.get<SentInvitation[]>(INVITE_COPY[direction].path) });

/** Pending invitations this business has sent. */
export function SentInvitations({ direction }: { direction: Direction }) {
  const sent = useSentInvitations(direction);
  const pending = sent.data?.filter((i) => i.status === 'PENDING') ?? [];
  if (!pending.length) return null;
  return (
    <div className="card">
      <h2>Invitations sent</h2>
      <ul className="list">
        {pending.map((i) => <li key={i.id}>{i.businessName} · {i.contactName} · {i.phoneMasked} <small className="muted">· {dt(i.createdAt)}</small></li>)}
      </ul>
    </div>
  );
}

/** Invite the other side of a fuel-credit relationship by the owner's mobile. */
export function InviteButton({ direction }: { direction: Direction }) {
  const qc = useQueryClient();
  const copy = INVITE_COPY[direction];
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ businessName: '', contactName: '', phone: '' });
  const [link, setLink] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <>
      <button className="btn primary" onClick={() => { setOpen(true); setLink(''); setF({ businessName: '', contactName: '', phone: '' }); }}>{copy.button}</button>
      <Dialog open={open} title={copy.title} onClose={() => setOpen(false)}>
        {link ? (
          <div className="stack">
            <div className="alert ok">Invitation created. If they already use SuperIndent, they will also see it in the app.</div>
            <p>Send them this link on WhatsApp so they can accept:</p>
            <input readOnly value={link} onFocus={(e) => e.target.select()} />
            <a className="btn primary" href={whatsappUrl(f.phone, `${copy.share}: ${link}`)} target="_blank" rel="noreferrer">Send to {f.contactName || 'them'} on WhatsApp</a>
            <a className="btn" href={`https://wa.me/?text=${encodeURIComponent(`${copy.share}: ${link}`)}`} target="_blank" rel="noreferrer">Share with someone else</a>
          </div>
        ) : (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void run(async () => { const r = await api.post<{ link: string }>(copy.path, f); setLink(r.link); await qc.invalidateQueries({ queryKey: ['invitations', direction] }); }); }}>
            <Field label={copy.name}><input value={f.businessName} onChange={(e) => setF({ ...f, businessName: e.target.value })} required minLength={2} /></Field>
            <Field label="Owner / contact name"><input value={f.contactName} onChange={(e) => setF({ ...f, contactName: e.target.value })} required /></Field>
            <Field label="Owner’s mobile" hint="They sign in with Google and confirm this mobile number to accept."><MobileInput value={f.phone} onChange={(phone) => setF({ ...f, phone })} required /></Field>
            <ErrorBox error={error} />
            <button className="btn primary" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</button>
          </form>
        )}
      </Dialog>
    </>
  );
}
